import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Link2,
  Copy,
  Check,
  ExternalLink,
  Sparkles,
  AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { Event } from '@/types/event.types';
import { getCelebrationInviteUrl } from '@/utils/inviteUrl';

export interface InviteLinkBarProps {
  celebrationId: number;
  celebrationTitle?: string;
  slug?: string | null;
  registrationToken?: string;
  events?: Event[];
  selectedEventId?: number;
  onSelectEventId?: (eventId: number | undefined) => void;
  compact?: boolean;
  className?: string;
  showEventFilter?: boolean;
}

export const InviteLinkBar: React.FC<InviteLinkBarProps> = ({
  celebrationId,
  slug,
  registrationToken,
  events = [],
  selectedEventId,
  onSelectEventId,
  compact = false,
  className = '',
  showEventFilter = true,
}) => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [internalEventId, setInternalEventId] = useState<number | undefined>(selectedEventId);

  // Sync internal event id when prop changes
  const activeEventId = selectedEventId !== undefined ? selectedEventId : internalEventId;

  const inviteUrl = useMemo(() => {
    return getCelebrationInviteUrl(slug, {
      eventId: activeEventId,
      token: registrationToken,
    });
  }, [slug, activeEventId, registrationToken]);

  const handleCopy = async () => {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy invite URL:', err);
    }
  };

  const handleOpen = () => {
    if (!inviteUrl) return;
    window.open(inviteUrl, '_blank', 'noopener,noreferrer');
  };

  const handleEventChange = (eventIdStr: string) => {
    const id = eventIdStr ? Number(eventIdStr) : undefined;
    setInternalEventId(id);
    onSelectEventId?.(id);
  };

  // State 1: Slug is not configured yet
  if (!slug || !slug.trim()) {
    return (
      <div
        className={`rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50/50 p-3.5 sm:p-4 text-amber-900 shadow-sm ${className}`}
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-start gap-2.5">
            <div className="rounded-lg bg-amber-100 p-2 text-amber-700 flex-shrink-0 mt-0.5 sm:mt-0">
              <AlertCircle className="h-4 w-4" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-sm text-amber-950">Guest Invite Link Not Configured</span>
                <Badge variant="outline" className="text-[10px] bg-amber-100/70 border-amber-300 text-amber-800">
                  Setup Required
                </Badge>
              </div>
              <p className="text-xs text-amber-800/90 mt-0.5">
                Set up a URL slug for your guest landing page to generate shareable invite links and enable online RSVPs.
              </p>
            </div>
          </div>

          <Button
            size="sm"
            onClick={() => navigate(`/celebrations/${celebrationId}/guest-landing`)}
            className="bg-amber-600 hover:bg-amber-700 text-white text-xs h-8 px-3.5 flex-shrink-0 self-start sm:self-center shadow-sm"
          >
            <Sparkles className="h-3.5 w-3.5 mr-1.5" />
            Set Up Invite Link
          </Button>
        </div>
      </div>
    );
  }

  // State 2: Compact Mode
  if (compact) {
    return (
      <div
        className={`rounded-lg border border-primary/20 bg-primary/[0.03] p-2.5 flex items-center justify-between gap-2 ${className}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Link2 className="h-4 w-4 text-primary flex-shrink-0" />
          <span className="text-xs font-mono text-primary truncate select-all">{inviteUrl}</span>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <Button
            size="xs"
            variant="outline"
            onClick={handleCopy}
            className="h-7 text-xs gap-1 border-primary/30 text-primary hover:bg-primary/10"
          >
            {copied ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
            {copied ? 'Copied' : 'Copy'}
          </Button>
          <Button
            size="xs"
            variant="ghost"
            onClick={handleOpen}
            className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
            title="Open Landing Page"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    );
  }

  // State 3: Standard Full Bar
  return (
    <div
      className={`rounded-xl border border-blue-100 bg-gradient-to-r from-blue-50/70 via-indigo-50/40 to-slate-50 p-3.5 sm:p-4 shadow-sm ${className}`}
    >
      <div className="flex flex-col gap-3">
        {/* Top Header Row */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="rounded-lg bg-blue-100 p-1.5 text-blue-700">
              <Link2 className="h-4 w-4" />
            </div>
            <span className="text-sm font-semibold text-slate-900">Registration Invite Link</span>
            <Badge variant="secondary" className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px] font-medium">
              ● Active &amp; Ready
            </Badge>
          </div>

          <div className="flex items-center gap-1.5 self-start sm:self-auto">
            <Button
              variant="ghost"
              size="xs"
              onClick={() => navigate(`/celebrations/${celebrationId}/guest-landing`)}
              className="text-xs text-muted-foreground hover:text-foreground h-7"
            >
              <Sparkles className="h-3 w-3 mr-1 text-primary" />
              Customize Landing Page
            </Button>
          </div>
        </div>

        {/* Link Box & Primary Controls */}
        <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2">
          {/* Optional Event Selector for multi-event celebrations */}
          {showEventFilter && events.length > 1 && (
            <div className="flex-shrink-0">
              <select
                aria-label="Filter invite link by event"
                value={activeEventId ?? ''}
                onChange={(e) => handleEventChange(e.target.value)}
                className="h-9 w-full md:w-auto rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="">All Events (Full Celebration)</option>
                {events.map((ev) => (
                  <option key={ev.id} value={ev.id}>
                    Event: {ev.title}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* URL Display Pill */}
          <div className="flex-1 min-w-0 flex items-center gap-2 rounded-lg border border-blue-200/80 bg-white px-3 py-1.5 shadow-sm">
            <span className="text-xs font-mono font-medium text-blue-700 truncate select-all flex-1">
              {inviteUrl}
            </span>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap">
            <Button
              size="sm"
              variant="default"
              onClick={handleCopy}
              className="h-9 px-3.5 text-xs gap-1.5 bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
            >
              {copied ? <Check className="h-3.5 w-3.5 text-white" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? 'Copied Link!' : 'Copy Link'}
            </Button>

            <Button
              size="sm"
              variant="outline"
              onClick={handleOpen}
              className="h-9 px-3 text-xs gap-1.5 bg-white border-slate-200 hover:bg-slate-50 text-slate-700"
              title="Open public guest page in new tab"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Preview
            </Button>

          </div>
        </div>
      </div>
    </div>
  );
};
